import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, FileText, ArrowRightCircle, Send, ThumbsUp, XCircle, Pencil, FileDown } from 'lucide-react';
import { erpList, erpPost, erpOpenPdf } from '../../../services/erpApi';
import { Card, Button, Input, PageHeader, Table, FormModal, Drawer, EmptyState, SearchBox, Segmented, fmtInr, fmtDate } from '../ui';
import { StatusBadge, PartyCell, DocNumber, Meta, Totals, DocLines } from '../components/Doc';
import { SalesDocForm } from './forms/SalesDocForm';

const FILTERS = [{ value: '', label: 'All' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'accepted', label: 'Accepted' }, { value: 'converted', label: 'Converted' }, { value: 'lost', label: 'Lost' }];

export const Quotations = ({ go }) => {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(''); const [q, setQ] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null); const [convert, setConvert] = useState(null); const [poNo, setPoNo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await erpList('quotations', { status: status || undefined, q: q || undefined }); setRows(r.data.items || []); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openNew = () => setModal({});
  const openEdit = (d) => { setModal(d); setView(null); };
  const act = async (path, body, msg) => {
    try { const r = await erpPost(path, body); toast.success(msg); setView(r.data); load(); return r.data; }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); return null; }
  };
  const doConvert = async () => {
    const so = await act(`quotations/${convert.id}/convert`, { customer_po_no: poNo }, 'Converted to Sales Order');
    if (so) { setConvert(null); setView(null); go(`sales/orders`); }
  };
  const columns = [
    { key: 'number', label: 'Quotation', render: (r) => <DocNumber number={r.number} date={r.date} /> },
    { key: 'customer', label: 'Customer', render: (r) => <PartyCell name={r.customer_name} code={r.customer_code} hidden={r.identity_hidden} /> },
    { key: 'items', label: 'Lines', align: 'right', render: (r) => r.items.length },
    { key: 'total', label: 'Total', align: 'right', render: (r) => <span className="font-bold">{fmtInr(r.total)}</span> },
    { key: 'valid_till', label: 'Valid till', render: (r) => fmtDate(r.valid_till) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="erp-quotations">
      <PageHeader crumb="Sales" title="Quotations" subtitle="Price offers to customers. Convert accepted quotes into Sales Orders in one click."
        actions={<Button icon={Plus} onClick={openNew} data-testid="qt-add">New Quotation</Button>} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Segmented testid="qt-filter" value={status} onChange={setStatus} options={FILTERS} />
        <SearchBox value={q} onChange={setQ} placeholder="Search number or customer…" className="w-72" data-testid="qt-search" />
      </div>
      <Card className="p-0 erp-rise erp-d1">
        <Table testid="qt-table" columns={columns} rows={rows} loading={loading} onRowClick={setView}
          empty={<EmptyState icon={FileText} title="No quotations" text="Create your first quotation to start the sales cycle." action={<Button icon={Plus} onClick={openNew}>New Quotation</Button>} />} />
      </Card>

      {modal && <SalesDocForm kind="quotation" initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}

      <Drawer open={!!view} onClose={() => setView(null)} title={view?.number} subtitle={view ? `Quotation · ${fmtDate(view.date)}` : ''} testid="qt-drawer">
        {view && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <StatusBadge status={view.status} testid="qt-drawer-status" />
              {view.so_number && <button type="button" onClick={() => go('sales/orders')} className="text-xs font-semibold underline" style={{ color: 'var(--erp-blue)' }}>→ {view.so_number}</button>}
              <span className="flex-1" />
              <Button variant="ghost" size="sm" icon={FileDown} onClick={() => erpOpenPdf(`quotations/${view.id}/pdf`)} data-testid="qt-pdf">PDF</Button>
              {['draft', 'sent', 'accepted'].includes(view.status) && <Button variant="ghost" size="sm" icon={Pencil} onClick={() => openEdit(view)} data-testid="qt-edit">Edit</Button>}
              {view.status === 'draft' && <Button variant="soft" size="sm" icon={Send} onClick={() => act(`quotations/${view.id}/status`, { status: 'sent' }, 'Marked as sent')} data-testid="qt-mark-sent">Mark Sent</Button>}
              {['draft', 'sent'].includes(view.status) && <Button variant="soft" size="sm" icon={ThumbsUp} onClick={() => act(`quotations/${view.id}/status`, { status: 'accepted' }, 'Marked accepted')} data-testid="qt-mark-accepted">Accepted</Button>}
              {['draft', 'sent', 'accepted'].includes(view.status) && <Button variant="ghost" size="sm" icon={XCircle} onClick={() => act(`quotations/${view.id}/status`, { status: 'lost' }, 'Marked lost')} data-testid="qt-mark-lost">Lost</Button>}
              {view.status !== 'converted' && view.status !== 'lost' && <Button size="sm" icon={ArrowRightCircle} onClick={() => { setConvert(view); setPoNo(''); }} data-testid="qt-convert">Convert to SO</Button>}
            </div>
            <Meta items={[['Customer', <PartyCell name={view.customer_name} code={view.customer_code} hidden={view.identity_hidden} />], ['Valid till', fmtDate(view.valid_till)], ['Tax type', view.intra_state ? 'CGST + SGST' : 'IGST'], ['Created by', view.created_by_name]]} />
            <DocLines items={view.items} />
            <Totals doc={view} />
            {view.notes && <p className="text-sm mt-4 p-3 rounded-lg" style={{ background: '#f5f7fb' }}>{view.notes}</p>}
          </>
        )}
      </Drawer>

      <FormModal open={!!convert} onClose={() => setConvert(null)} onSubmit={doConvert} testid="qt-convert-modal" width="max-w-md" submitLabel="Create Sales Order" title="Convert to Sales Order" subtitle={convert?.number}>
        <Input label="Customer PO number" value={poNo} onChange={(e) => setPoNo(e.target.value)} placeholder="Optional — blocks duplicate POs" data-testid="qt-convert-po" />
      </FormModal>
    </div>
  );
};
